import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS, createApiUrl, getAuthConfig, apiRequest } from '../../../api/config';
import { useTheme } from '../ThemeContext';
import { getTranslation } from '../translations';  // 导入翻译函数

// 导入样式文件
import '../../../styles/dashboard.css';  // 仪表盘样式
import '../../../styles.css';  // 全局样式

// 为Toast组件添加的CSS样式
const toastStyles = `
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translate3d(0, -20px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes fadeOutUp {
    from {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
    to {
      opacity: 0;
      transform: translate3d(0, -20px, 0);
    }
  }

  .animate-fade-in-down {
    animation: fadeInDown 0.3s ease-out forwards;
  }

  .animate-fade-out-up {
    animation: fadeOutUp 0.3s ease-in forwards;
  }
`;

// Toast提示组件
const Toast = ({ message, type = 'success', onClose }) => {
  const { darkMode } = useTheme();
  const [isClosing, setIsClosing] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // 与CSS动画持续时间一致
  };
  
  const typeClass = type === 'success' ? 'toast-success' : type === 'error' ? 'toast-error' : 'toast-warning';
  
  return (
    <div className={`toast-container ${isClosing ? 'toast-closing' : 'toast-entering'}`}>
      <div className={`toast ${typeClass}`}>
        <div className="toast-icon">
          {type === 'success' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {type === 'error' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {type === 'warning' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
        </div>
        <div className="toast-message">
          <p className="toast-text">{message}</p>
        </div>
        <button className="toast-close" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// 套餐特性检查图标组件
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="plan-feature-icon plan-feature-icon-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

// 套餐特性叉图标组件
const CrossIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="plan-feature-icon plan-feature-icon-cross" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// 套餐特性 — 价格分档生成函数，与 LandingPage Pricing 组件完全一致
// 档位: 体验版($0) / 入门版($299) / 成长版($799) / 专业版($1999) / 旗舰版($3999)
const getPlanFeatures = (plan, lang = 'zh') => {
  const features = [];
  const price = Number(plan.price) || 0;
  const totalCalls = plan?.totalCalls ?? 0;
  const isUnlimitedCalls = totalCalls === 0;

  // 地理定向
  if (price < 500) {
    features.push({ text: lang === 'zh' ? '基础地理定向' : 'Basic Geo-Targeting', enabled: true });
  } else {
    features.push({ text: lang === 'zh' ? '全球地理定向' : 'Global Geo-Targeting', enabled: true });
  }

  // UA识别拦截
  features.push({ text: lang === 'zh' ? 'UA 识别拦截' : 'UA Detection & Block', enabled: true });

  // 访问日志：1天→30天→90天→180天→365天
  if (isUnlimitedCalls || price >= 3999) {
    features.push({ text: lang === 'zh' ? '访问日志 365 天' : 'Access Logs 365 days', enabled: true });
  } else if (price >= 1999) {
    features.push({ text: lang === 'zh' ? '访问日志 180 天' : 'Access Logs 180 days', enabled: true });
  } else if (price >= 799) {
    features.push({ text: lang === 'zh' ? '访问日志 90 天' : 'Access Logs 90 days', enabled: true });
  } else if (price >= 299) {
    features.push({ text: lang === 'zh' ? '访问日志 30 天' : 'Access Logs 30 days', enabled: true });
  } else if (price > 0) {
    features.push({ text: lang === 'zh' ? '访问日志 30 天' : 'Access Logs 30 days', enabled: true });
  } else {
    features.push({ text: lang === 'zh' ? '访问日志 1 天' : 'Access Logs 1 day', enabled: true });
  }

  // 斗篷 + API + 子账号
  if (price >= 3999) {         // 旗舰版 $3999
    features.push({ text: lang === 'zh' ? '无限斗篷数量' : 'Unlimited Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: true });
    features.push({ text: lang === 'zh' ? '10 个子账号' : '10 Sub-accounts', enabled: true });
  } else if (price >= 1999) {  // 专业版 $1999
    features.push({ text: lang === 'zh' ? '最多 300 个斗篷' : 'Up to 300 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: true });
    features.push({ text: lang === 'zh' ? '3 个子账号' : '3 Sub-accounts', enabled: true });
  } else if (price >= 799) {   // 成长版 $799
    features.push({ text: lang === 'zh' ? '最多 100 个斗篷' : 'Up to 100 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: true });
    features.push({ text: lang === 'zh' ? '子账号' : 'Sub-accounts', enabled: false });
  } else if (price >= 299) {   // 入门版 $299
    features.push({ text: lang === 'zh' ? '最多 10 个斗篷' : 'Up to 10 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: false });
    features.push({ text: lang === 'zh' ? '子账号' : 'Sub-accounts', enabled: false });
  } else {                     // 体验版 $0
    features.push({ text: lang === 'zh' ? '最多 5 个斗篷' : 'Up to 5 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: false });
    features.push({ text: lang === 'zh' ? '子账号' : 'Sub-accounts', enabled: false });
  }

  return features;
};

// 套餐卡片组件
const PlanCard = ({ plan, current, onSelect }) => {
  const { language } = useTheme();
  
  // 多语言名称
  const displayName = language === 'zh' ? plan.name : (plan.nameEn || plan.name);
  const totalCalls = plan?.totalCalls ?? 0;
  const isUnlimited = totalCalls === 0;
  const callsText = isUnlimited ? (language === 'zh' ? '不限次数' : 'Unlimited') : `${totalCalls.toLocaleString()}${language === 'zh' ? ' 次' : ''}`;
  const validityText = plan.randomPeriod ? null : `${plan.validityDays}${language === 'zh' ? ' 天' : ' days'}`;

  // 使用统一分档特性
  const features = getPlanFeatures(plan, language).filter(f => !f.text.includes('子账号'));

  return (
    <div className={`plan-card ${current ? 'featured' : ''}`}>
      {current && (
        <div className="plan-card-badge">
          <span className="badge badge-blue">{language === 'zh' ? '当前套餐' : 'Current'}</span>
        </div>
      )}
      
      <div className="plan-name">{displayName}</div>
      <div className="plan-price">
        <sup>$</sup>{plan.price}
      </div>
      {!plan.randomPeriod && <div className="plan-period">/{plan.validityDays}{language === 'zh' ? '天' : ''}</div>}
      <div className="plan-quota">{isUnlimited ? (language === 'zh' ? '不限' : 'Unlimited') : totalCalls.toLocaleString()}</div>
      <div className="plan-quota-label">{language === 'zh' ? '可用次数' : 'Quota'}</div>
      
      <div className="plan-features">
        {features.map((feat, idx) => (
          <div key={idx} className={`plan-feat ${!feat.enabled ? 'plan-feat-disabled' : ''}`}>
            <span className={feat.enabled ? 'check' : 'cross'}>{feat.enabled ? '✓' : '✕'}</span>
            <span style={{ opacity: feat.enabled ? 1 : 0.45 }}>{feat.text}</span>
          </div>
        ))}
      </div>
      
      <button className="plan-buy-btn" onClick={() => onSelect(plan.id)}>
        {current ? (language === 'zh' ? '续费' : 'Renew') : (language === 'zh' ? '购买' : 'Buy')}
      </button>
    </div>
  );
};

const CloakPlans = () => {
  const { darkMode, language } = useTheme();
  const t = getTranslation(language);  // 创建翻译函数
  const [searchParams] = useSearchParams();  // 获取URL参数
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userStatus, setUserStatus] = useState({
    isMember: false,
    membershipInfo: null
  });
  // 用户统计信息（剩余次数等，来自 /api/user/statistics）
  const [userStats, setUserStats] = useState({
    totalCalls: 0,
    remainingCalls: 0
  });
  const [paymentData, setPaymentData] = useState({
    remark: '',
    screenshot: null
  });
  const [userPayments, setUserPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // 防止 StrictMode 双重执行
  const lastDepsRef = useRef(null);
  
  // Toast通知状态
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success'
  });
  
  // 将样式添加到文档
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'toast-animations';
    styleElement.textContent = toastStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      const existingStyle = document.getElementById('toast-animations');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);
  
  // 显示Toast通知的函数
  const showToast = (message, type = 'success') => {
    setToast({
      visible: true,
      message,
      type
    });
  };
  
  // 关闭Toast通知的函数
  const closeToast = () => {
    setToast(prev => ({
      ...prev,
      visible: false
    }));
  };
  
  // TRC20钱包地址 - 实际应用中应从配置或API获取
  const walletAddress = 'TPUC9TvsbMxS2NoAAahMtfxqaPbpUHVSqi';
  
  // 加载套餐数据和用户信息
  useEffect(() => {
    // 防止 StrictMode 开发环境下重复调用
    if (lastDepsRef.current === language) return;
    lastDepsRef.current = language;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 获取套餐列表
        const plansResult = await apiRequest(API_ENDPOINTS.PLAN.LIST, {}, true);
        
        // Java后端返回 {status, data} 格式，Python返回 {items}
        const plansData = plansResult?.data || plansResult?.items || [];
        if (plansData.length > 0) {
          setPlans(plansData);
        }
        
        // 获取用户信息（包括会员状态）
        const userResult = await apiRequest(API_ENDPOINTS.USER.PROFILE, {}, true);
        
        if (userResult && userResult.data) {
          setUserStatus({
            isMember: userResult.data.isMember || false,
            membershipInfo: userResult.data.membershipInfo || null
          });
          
          // 如果用户是会员，设置当前套餐信息
          if (userResult.data.isMember && userResult.data.membershipInfo && plansData.length > 0) {
            // 根据会员信息中的planId找到对应的套餐
            const userPlan = plansData.find(
              p => p.id === userResult.data.membershipInfo.planId
            );
            
            if (userPlan) {
              setCurrentPlan({
                ...userPlan,
                startDate: new Date(userResult.data.membershipInfo.startDate),
                endDate: new Date(userResult.data.membershipInfo.endDate)
              });
            }
          }
        }
        
        // 获取用户统计信息（真实剩余次数，与管理中心首页一致）
        try {
          const statsResult = await apiRequest(
            API_ENDPOINTS.USER.STATISTICS,
            {},
            true,
            'GET'
          );
          
          if (statsResult && statsResult.status === 'success' && statsResult.data) {
            const { user: statsUser } = statsResult.data;
            setUserStats({
              totalCalls: statsUser.totalCalls || 0,
              remainingCalls: statsUser.remainingCalls || 0
            });
          }
        } catch (statsError) {
          console.warn('[CloakPlans] 获取用户统计信息失败:', statsError);
          // 不影响主流程，使用默认值
        }
        
        // 获取用户的支付记录
        await fetchUserPayments();
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to load data:', error);
        setError(t('failedToLoadData'));
        setLoading(false);
      }
    };
    
    fetchData();
  }, [language]); // 添加language作为依赖，在语言变化时重新加载
  
  // 处理从套餐购买页面跳转过来的购买请求
  useEffect(() => {
    const buyPlanId = searchParams.get('buy');
    if (buyPlanId && plans.length > 0) {
      const plan = plans.find(p => p.id === buyPlanId || p.id === Number(buyPlanId));
      if (plan) {
        setSelectedPlan(plan.id);
        setShowPaymentModal(true);
      }
    }
  }, [searchParams, plans]);
  
  // 获取用户支付记录
  const fetchUserPayments = async () => {
    try {
      setPaymentsLoading(true);
      const paymentsResult = await apiRequest(API_ENDPOINTS.PLAN.MY_PAYMENTS, {}, true);
      
      // Java后端返回 {status, data} 格式
      const paymentsData = paymentsResult?.data || paymentsResult?.items || [];
      if (paymentsData.length > 0) {
        setUserPayments(paymentsData);
      }
      setPaymentsLoading(false);
    } catch (error) {
      console.error('Failed to load payment records:', error);
      setPaymentsLoading(false);
    }
  };
  
  // 处理支付提交成功后刷新支付记录
  const refreshAfterPayment = async () => {
    await fetchUserPayments();
  };
  
  // 格式化日期
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  
  // 处理套餐选择 - 直接打开支付弹窗
  const handleSelectPlan = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (plan && plan.price === 0 && userStatus.isMember) {
      showToast(t('alreadySubscribed'), 'error');
      return;
    }
    setSelectedPlan(planId);
    setShowPaymentModal(true);
  };
  
  // 处理取消支付
  const handleCancelPayment = () => {
    setShowPaymentModal(false);
    setPaymentData({
      remark: '',
      screenshot: null
    });
  };
  
  // 处理支付截图上传
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentData({
        ...paymentData,
        screenshot: e.target.files[0]
      });
    }
  };
  
  // 处理文本输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({
      ...paymentData,
      [name]: value
    });
  };
  
  // 处理套餐购买/升级提交
  const handlePaymentSubmit = async () => {
    try {
      if (!paymentData.screenshot) {
        showToast(t('pleaseUploadPaymentScreenshot'), 'error');
        return;
      }
      
      // 创建FormData对象
      const formData = new FormData();
      formData.append('planId', selectedPlan);
      formData.append('remark', paymentData.remark || '');
      formData.append('screenshot', paymentData.screenshot);
      
      // 调用支付API
      const result = await apiRequest(
        API_ENDPOINTS.PLAN.SUBMIT_PAYMENT, 
        formData,
        true,
        'POST',
        true // 表示使用FormData格式
      );
      
      if (result && result.status === 'success') {
        setShowPaymentModal(false);
        setPaymentData({
          remark: '',
          screenshot: null
        });
        setShowSuccessModal(true);
        
        refreshAfterPayment();
      } else {
        throw new Error(result.error || t('failedToSubmitPaymentInformation'));
      }
    } catch (error) {
      console.error('Failed to submit payment information:', error);
      showToast(error.message || t('failedToSubmitPaymentInformation'), 'error');
    }
  };
  
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="page-state">
        <p className="page-state-error">{error}</p>
        <button 
          className="dashboard-btn primary-btn"
          onClick={() => window.location.reload()}
        >
          {t('retry')}
        </button>
      </div>
    );
  }

  // 如果没有套餐数据
  if (plans.length === 0) {
    return (
      <div className="page-state">
        <p className="page-state-text">{t('noPlanData')}</p>
      </div>
    );
  }
  
  return (
    <div className="page active">
      <div className="page-hd">
        <div>
          <div className="page-title">{t('myPlans')}</div>
        </div>
      </div>

      {/* 顶部三个信息卡片 */}
      {userStatus.isMember && currentPlan ? (
        <div className="plan-info-grid mb-18">
          {/* 当前套餐 */}
          <div className="plan-info-card">
            <div className="plan-info-label">{t('currentPlan')}</div>
            <div className="plan-info-value plan-info-value-blue">
              {language === 'zh' ? currentPlan.name : (currentPlan.nameEn || currentPlan.name)}
            </div>
            <div className="plan-info-sub">
              {t('expiryDate')}: {formatDate(currentPlan.endDate)}
            </div>
          </div>

          {/* 剩余使用次数 — 使用 /api/user/statistics 真实数据 */}
          <div className="plan-info-card">
            <div className="plan-info-label">{t('remainingUsage')}</div>
            <div className="plan-info-value plan-info-value-green">
              {userStats.totalCalls === 0 ? t('unlimited') : userStats.remainingCalls.toLocaleString()}
            </div>
            <div className="plan-progress-bar">
              <div
                className="plan-progress-fill"
                style={{
                  width: `${(() => {
                    if (userStats.totalCalls === 0) return 100; // 无限不显示进度
                    const total = userStats.totalCalls || 1;
                    const remaining = userStats.remainingCalls || 0;
                    const used = Math.max(0, total - remaining);
                    return Math.min(100, (used / total) * 100);
                  })()}%`
                }}
              />
            </div>
            <div className="plan-info-sub">
              {userStats.totalCalls === 0 ? t('unlimited') : 
                t('usedText', {
                  used: Math.max(0, (userStats.totalCalls || 0) - (userStats.remainingCalls || 0)).toLocaleString(),
                  total: (userStats.totalCalls || 0).toLocaleString()
                })
              }
            </div>
          </div>

          {/* 有效期 */}
          <div className="plan-info-card">
            <div className="plan-info-label">{t('validity')}</div>
            <div className="plan-info-value plan-info-value-orange">
              {(() => {
                const end = new Date(currentPlan.endDate);
                const now = new Date();
                const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
                return `${Math.max(0, diff)} ${t('days')}`;
              })()}
            </div>
            <div className="plan-info-sub">
              {formatDate(currentPlan.startDate)} → {formatDate(currentPlan.endDate)}
            </div>
          </div>
        </div>
      ) : (
        <div className="form-warn mb-18">
          {t('notSubscribed')}
        </div>
      )}

      {/* 套餐购买历史表格 */}
      <div className="table-wrap mb-18">
        <div className="table-toolbar">
          <span className="table-toolbar-title">{t('purchaseHistory')}</span>
          {paymentsLoading && <span className="badge badge-blue">{t('loading')}</span>}
        </div>
        {userPayments.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>{t('orderId')}</th>
                <th>{t('planName')}</th>
                <th>{t('calls')}</th>
                <th>{t('amount')}</th>
                <th>{t('validityPeriod')}</th>
                <th>{t('purchaseTime')}</th>
                <th>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {userPayments.map((payment) => (
                <tr key={payment.id}>
                  <td className="td-mono">{payment.orderId || `ORD-${payment.id}`}</td>
                  <td>{payment.planName || t('unknownPlan')}</td>
                  <td>{payment.totalCalls ? payment.totalCalls.toLocaleString() : '-'}</td>
                  <td>${payment.amount || 0}</td>
                  <td>{payment.validityDays ? `${payment.validityDays}${t('daysText')}` : '-'}</td>
                  <td className="td-mono">
                    {payment.createdAt ? formatDate(payment.createdAt) : t('unknownTime')}
                  </td>
                  <td>
                    <span className={
                      payment.status === 'approved' ? 'badge badge-green' :
                      payment.status === 'rejected' ? 'badge badge-red' : 'badge badge-orange'
                    }>
                      {payment.status === 'pending' ? t('pending') : 
                       payment.status === 'approved' ? t('approved') : 
                       payment.status === 'rejected' ? t('rejected') : payment.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">
            {paymentsLoading ? t('loading') : t('noPaymentRecords')}
          </div>
        )}
      </div>
      
      {/* 支付信息弹窗 */}
      {showPaymentModal && (
        <div className="modal-overlay open" onClick={handleCancelPayment}>
          <div className="modal">
            <div className="modal-hd">
              <span className="modal-title">{t('paymentInformation')}</span>
              <span className="modal-close" onClick={handleCancelPayment}>×</span>
            </div>
            <div className="modal-body">
              <div className="mb-4">
                <p className="form-label-text">
                  {t('selectedPlanText', { 
                    planName: (() => {
                      const selectedPlanObj = plans.find(p => p.id === selectedPlan);
                      return language === 'zh' ? 
                        selectedPlanObj?.name : 
                        (selectedPlanObj?.nameEn || selectedPlanObj?.name);
                    })()
                  })}
                </p>
              </div>
              
              <div className="form-space">
                {/* 钱包地址 */}
                <div className="payment-wallet-box">
                  <label className="fi-label">
                    {t('walletAddress')}
                  </label>
                  <div className="flex mb-3">
                    <input
                      type="text"
                      readOnly
                      value={walletAddress}
                      className="fi-block"
                    />
                    <button
                      className="btn btn-primary"
                      style={{marginLeft: '8px', whiteSpace: 'nowrap'}}
                      onClick={() => {
                        navigator.clipboard.writeText(walletAddress);
                        showToast(t('walletAddressCopied'), 'success');
                      }}
                    >
                      {t('copy')}
                    </button>
                  </div>
                
                  {/* 二维码 */}
                  <div className="flex justify-center my-2">
                    <div className="text-center">
                      <span className="block fi-label mb-3">
                        {t('scanQrCode')}
                      </span>
                      <img
                        src="/qrcode.png"
                        alt={t('walletQrCode')}
                        className="payment-qr-code"
                      />
                      <span className="block fi-hint mt-2">
                        {t('useUsdtTrc20')}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* 购买金额 */}
                <div>
                  <label className="fi-label">
                    {t('paymentAmount')}
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      readOnly
                      value={`$${plans.find(p => p.id === selectedPlan)?.price || 0}`}
                      className="fi-block"
                    />
                    <span className="ml-2 fi-hint">USDT</span>
                  </div>
                </div>
                
                {/* 支付截图上传 */}
                <div>
                  <label className="fi-label">
                    {t('uploadPaymentScreenshot')}
                  </label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn btn-default"
                    >
                      {t('chooseFile')}
                    </button>
                    <span className="ml-3 fi-hint">
                      {paymentData.screenshot ? paymentData.screenshot.name : t('noFileSelected')}
                    </span>
                  </div>
                  {paymentData.screenshot && (
                    <div className="mt-2">
                      <img
                        src={URL.createObjectURL(paymentData.screenshot)}
                        alt={t('paymentScreenshotPreview')}
                        className="payment-preview-image"
                      />
                    </div>
                  )}
                </div>
                
                {/* 备注 */}
                <div>
                  <label className="fi-label">
                    {t('noteOptional')}
                  </label>
                  <textarea
                    name="remark"
                    value={paymentData.remark}
                    onChange={handleInputChange}
                    rows="3"
                    className="fi-block fi-block-textarea"
                    placeholder={t('noteOptionalPlaceholder')}
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-default"
                onClick={handleCancelPayment}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={handlePaymentSubmit}
              >
                {t('submitPaymentInfo')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 提交成功弹窗 */}
      {showSuccessModal && (
        <div className="modal-overlay open" onClick={() => setShowSuccessModal(false)}>
          <div className="modal modal-sm text-center">
            <div className="success-modal-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="success-modal-title">
              {t('submittedSuccess')}
            </h3>
            <p className="success-modal-message">
              {t('paymentSubmittedMessage')}
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="btn btn-success"
            >
              {t('gotIt')}
            </button>
          </div>
        </div>
      )}

      {/* Toast提示 */}
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default CloakPlans;